import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	MatterMortgageType,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { CloserSyncTransaction } from '@models/mortgages/closer-sync-instruction';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { CloserSyncTransactionRepoService } from '@repos/mortgages/repos/closerSyncTransactionRepo';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'app-llc-data',
	templateUrl: './llc-data.component.html',
	styleUrls: ['./llc-data.component.scss'],
	standalone: false,
})
export class LlcDataComponent implements OnInit {
	@Select(MatterMortgagesState.getMortgageWithAssociations)
	mortgageFilterFn$: Observable<(id: string) => MatterMortgageWithAssociations>;
	@Select(LLCMortgageState.getLLCMortgage) llcMortgageFilterFn$: Observable<
		(id) => LLCTransaction
	>;
	llcMortgage$: Observable<LLCTransaction>;
	closerMortgage: MatterMortgageWithAssociations;
	llcJSONText: string;
	closerMortgageJSONText: string;
	closerSyncJSONText: string;
	MatterMortgageType = MatterMortgageType;
	closerSyncJSON: any = {};
	loadingCloserSync = false;

	constructor(
		private router: ActivatedRoute,
		private clipboardService: ClipboardService,
		private closerSyncTransactionRepoService: CloserSyncTransactionRepoService,
	) {}

	async ngOnInit(): Promise<void> {
		this.router.params.pipe(untilDestroyed(this)).subscribe(async (params) => {
			if (params?.mortgageId) {
				this.getMortgage$(params.mortgageId)
					.pipe(
						tap(
							(_m) =>
								(this.closerMortgageJSONText = JSON.stringify(_m, null, 2)),
						),
					)
					.subscribe(async (mortgage) => {
						this.closerMortgage = mortgage;
						if (mortgage.type === MatterMortgageType.LLC) {
							this.llcMortgage$ = this.getLLCMortgage$(
								mortgage.relatedMortgageId,
							);
						} else if (mortgage.type === MatterMortgageType.CLOSER_SYNC) {
							this.loadingCloserSync = true;
							let transaction: CloserSyncTransaction;
							try {
								const transactionDoc =
									await this.closerSyncTransactionRepoService
										.Get(mortgage.relatedMortgageId)
										.toPromise();
								transaction = transactionDoc.data() as CloserSyncTransaction;
								const instructionDoc =
									await this.closerSyncTransactionRepoService
										.getInstructionByVersionId(
											mortgage.relatedMortgageId,
											transaction.currentAssignedVersionId,
										)
										.toPromise();
								this.closerSyncJSON = {
									...transaction,
									instruction: instructionDoc.data(),
								};
								this.closerSyncJSONText = JSON.stringify(
									this.closerSyncJSON,
									null,
									2,
								);
							} catch (err) {
								console.log(
									'Unable to load closer sync mortgage',
									mortgage.relatedMortgageId,
									transaction?.currentAssignedVersionId,
									err,
								);
							} finally {
								this.loadingCloserSync = false;
							}
						}
					});
			}
		});
	}

	copyCloserSyncJSON() {
		this.clipboardService.copyText(this.closerSyncJSONText);
	}

	getLLCMortgage$(id: string): Observable<LLCTransaction> {
		return this.llcMortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((fn) => fn(id)),
			tap((_m) => (this.llcJSONText = JSON.stringify(_m, null, 2))),
		);
	}

	getMortgage$(id: string) {
		return this.mortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}

	copyLLCJSON() {
		this.clipboardService.copyText(this.llcJSONText);
	}

	copyCloserMortgageJSON() {
		this.clipboardService.copyText(this.closerMortgageJSONText);
	}
}
